import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  type CustomRoleTaxonomySelection,
  ROLE_SELECTION_LIST_QUERY,
  ROLE_V2_SELECTION_LIST_QUERY,
} from '@revelio/data-access';
import type { SelectionCategories, SelectionList } from '@revelio/iso-utility';

import { isV3Taxonomy } from '../utils/job-taxonomy-version-migration';
import {
  getRoleSelectionKey,
  transformRoleSelectionListForV3,
} from '../utils/role-taxonomy-utils';

const DEFAULT_TAXONOMY_ID = 0;

const convertTaxonomyIdToSelection = (
  taxonomyId: number
): CustomRoleTaxonomySelection => {
  if (taxonomyId === DEFAULT_TAXONOMY_ID) {
    return { default: true };
  } else if (taxonomyId >= 1 && taxonomyId <= 50) {
    return { ricsK50Id: taxonomyId.toString() };
  } else if (taxonomyId >= 51 && taxonomyId <= 60) {
    return { ricsK10Id: (taxonomyId - 50).toString() };
  } else if (taxonomyId > 60) {
    return { taxonomyId };
  }
  return { default: true };
};

/**
 * Fetches role selection lists for both V2 and V3 taxonomy.
 * Taxonomy version + id are passed explicitly by callers.
 */
export const useRoleSelectionLists = (
  requiredRoleLists: string[] = [],
  options: { jobTaxonomyVersion?: string; taxonomyId?: number }
) => {
  const isV3 = isV3Taxonomy(options.jobTaxonomyVersion);
  const taxonomyId = options.taxonomyId;

  const taxonomySelection =
    isV3 && taxonomyId !== undefined && taxonomyId !== null
      ? convertTaxonomyIdToSelection(taxonomyId)
      : undefined;

  const [{ data: v3RoleSelectionLists }] = useQuery({
    query: ROLE_SELECTION_LIST_QUERY,
    variables: { taxonomyId: taxonomySelection as CustomRoleTaxonomySelection },
    pause: !taxonomySelection,
  });

  const [{ data: legacyData }] = useQuery({
    query: ROLE_V2_SELECTION_LIST_QUERY,
    pause: isV3 || requiredRoleLists.length === 0,
  });

  const transformedRoleLists = useMemo(() => {
    const result: Record<string, SelectionList> = {};

    if (isV3 && v3RoleSelectionLists?.selectionList) {
      requiredRoleLists.forEach((listId) => {
        const v3ListId = getRoleSelectionKey(
          listId as SelectionCategories,
          true
        );
        const roleList =
          v3RoleSelectionLists.selectionList?.[
            v3ListId as keyof typeof v3RoleSelectionLists.selectionList
          ];

        if (roleList && Array.isArray(roleList)) {
          result[listId] = transformRoleSelectionListForV3(
            roleList,
            listId as SelectionCategories
          );
        }
      });
    } else if (!isV3 && legacyData?.selectionList) {
      requiredRoleLists.forEach((listId) => {
        const roleList =
          legacyData.selectionList?.[
            listId as keyof NonNullable<typeof legacyData.selectionList>
          ];

        if (roleList && Array.isArray(roleList)) {
          result[listId] = {
            id: listId,
            label: listId,
            value: roleList,
          } as SelectionList;
        }
      });
    }

    return result;
  }, [isV3, v3RoleSelectionLists, legacyData, requiredRoleLists]);

  return {
    roleSelectionLists: transformedRoleLists,
    isV3Taxonomy: isV3,
    taxonomyId: taxonomyId ?? undefined,
  };
};
